import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useIsMobile } from "@/hooks/useIsMobile";
import RegistryDrawer from "@/components/RegistryDrawer";
import ShortcutHelp from "@/components/ShortcutHelp";
import ProjectSwitcher from "@/components/ProjectSwitcher";
import WhatsNew from "@/components/WhatsNew";
import ThemeToggle from "@/components/ThemeToggle";
import Breadcrumbs from "@/components/Breadcrumbs";
import RecentActivity from "@/components/RecentActivity";
import SessionWatch from "@/components/SessionWatch";
import BackToTop from "@/components/BackToTop";
import Avatar from "@/components/Avatar";
import CurrencySelect from "@/components/CurrencySelect";
import TopRightCluster from "@/components/TopRightCluster";
import DepthTip from "@/components/DepthTip";
import { DemoBanner } from "@/lib/demo";
import { LayoutDashboard, Shield, Settings, LogOut, Sparkles, Search, Store, Server, Receipt, Users, Smartphone, Hash, Mic, Menu, X, User, PanelLeftClose, PanelLeftOpen, LayoutGrid } from "lucide-react";

export function DepthLabel({ depth }) {
  const map = { DEEP: "k-badge-accent", STANDARD: "", BASELINE: "k-badge-sandbox" };
  return <span className={`k-badge ${map[depth] || ""}`} data-testid="depth-label">{depth || "—"}</span>;
}

export function Balance({ orgId }) {
  const [bal, setBal] = useState(null);
  const [low, setLow] = useState(false);
  const [lowAt, setLowAt] = useState(100);
  // Item 1 — persistent balance: this lives in the Shell sidebar, so it is visible on every
  // authenticated screen, not only when the Billing/Credits page is open. Refreshes on focus.
  const refresh = () => {
    if (!orgId) return;
    api.get(`/orgs/${orgId}/balance`).then(r => {
      setBal(r.data.balance_credits); setLow(!!r.data.is_low); setLowAt(r.data.low_balance_at ?? 100);
    }).catch(() => {});
  };
  useEffect(() => {
    refresh();
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
    // eslint-disable-next-line
  }, [orgId]);
  return (
    <div data-testid="credit-balance-wrap">
      <div className={`k-badge ${low ? "k-badge-sandbox" : "k-badge-accent"}`} data-testid="credit-balance">
        <Sparkles size={13} /> {bal != null ? Number(bal).toLocaleString("en-GB") : "…"} credits
      </div>
      {/* Item 2 — calm, factual low-balance notice (no urgency framing), matching every other threshold notice. */}
      {low && <div className="k-muted" data-testid="low-balance-nudge" style={{ fontSize: 11, marginTop: 6 }}>
        You have {Number(bal).toLocaleString("en-GB")} credits left (below {Number(lowAt).toLocaleString("en-GB")}). You can top up whenever it suits you.</div>}
      <NavLink to="/billing" data-testid="balance-topup-link" className="k-link"
        style={{ fontSize: 12, display: "inline-block", marginTop: 6 }}>Top up / invoices →</NavLink>
    </div>
  );
}

function DisclaimerModal({ onClose }) {
  const [page, setPage] = useState(null);
  useEffect(() => { api.get("/legal/disclaimer").then(r => setPage(r.data)).catch(() => {}); }, []);
  const sections = page?.sections;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 70 }} onClick={onClose}>
      <div className="k-container k-fadein" data-testid="disclaimer-modal" onClick={e => e.stopPropagation()}
        style={{ maxWidth: 720, margin: "40px auto", width: "calc(100% - 32px)", padding: 28, maxHeight: "85vh", overflowY: "auto" }}>
        <div className="k-badge k-badge-sandbox" style={{ marginBottom: 10 }}>{page?.label}</div>
        <span className="k-muted" style={{ fontSize: 12, marginLeft: 8 }}>Review status: {page?.review_status}</span>
        {sections
          ? sections.map(([h, body]) => (
              <div key={h} style={{ marginTop: 14 }}>
                <div className="k-heading" style={{ fontWeight: 700, fontSize: 14 }}>{h}</div>
                <p className="k-muted" style={{ whiteSpace: "pre-line", fontSize: 13, lineHeight: 1.6, marginTop: 4 }}>{body}</p>
              </div>
            ))
          : <pre className="k-muted" style={{ whiteSpace: "pre-wrap", fontSize: 13, lineHeight: 1.6, fontFamily: "inherit", marginTop: 12 }}>{page?.body}</pre>}
        <button className="k-btn k-btn-ghost" data-testid="disclaimer-close" style={{ marginTop: 12 }} onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

export default function Shell({ children, active, toolbar, bare }) {
  const { user, org, logout } = useAuth();
  const nav = useNavigate();
  const [palette, setPalette] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [appsOpen, setAppsOpen] = useState(false);
  const isMobile = useIsMobile(768);
  // Desktop-only: collapse the left nav to an icon rail (labels appear on hover via title tooltips).
  // Preference persists per browser; a toggle at the top restores the full menu.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("kadana_nav_collapsed") === "1"; } catch { return false; }
  });
  const toggleCollapse = () => setCollapsed(c => {
    const n = !c;
    try { localStorage.setItem("kadana_nav_collapsed", n ? "1" : "0"); } catch { /* ignore */ }
    return n;
  });

  // Command palette (Cmd/Ctrl+K) — a second entry point to the SAME catalogue search (X7).
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setPalette(p => !p); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Close the mobile drawer whenever the active section changes (i.e. after navigation).
  useEffect(() => { setDrawer(false); }, [active]);

  const items = [
    { to: "/workspace", label: "Workspace", icon: LayoutDashboard, key: "workspace" },
    { to: "/marketplace", label: "Marketplace", icon: Store, key: "marketplace" },
    { to: "/hiring", label: "Hire", icon: Users, key: "hiring" },
    { to: "/hosting", label: "Hosting", icon: Server, key: "hosting" },
    { to: "/mobile", label: "Mobile", icon: Smartphone, key: "mobile" },
    { to: "/ussd", label: "USSD", icon: Hash, key: "ussd" },
    { to: "/media", label: "Media", icon: Mic, key: "media" },
    { to: "/billing", label: "Billing", icon: Receipt, key: "billing" },
    { to: "/profile", label: "Profile", icon: User, key: "profile", sub: true },
  ];
  if (user?.platform_role === "admin")
    items.push({ to: "/admin", label: "Admin", icon: Settings, key: "admin" });

  const renderSidebar = (collapsed) => (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 8,
        justifyContent: collapsed ? "center" : "space-between", marginBottom: 20 }}>
        {!collapsed && (
          <span className="k-heading" style={{ fontWeight: 700, fontSize: 22, letterSpacing: "-.02em",
            display: "inline-block", borderBottom: "3px solid var(--accent-primary)", paddingBottom: 3 }}>Kadana</span>
        )}
        {!isMobile && (
          <button className="k-btn k-btn-ghost" data-testid="nav-collapse-toggle" onClick={toggleCollapse}
            title={collapsed ? "Expand menu" : "Collapse menu"}
            aria-label={collapsed ? "Expand navigation menu" : "Collapse navigation menu"}
            style={{ padding: "6px 8px", minHeight: 0 }}>
            {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
          </button>
        )}
      </div>
      <button className="k-control" data-testid="command-palette-open" onClick={() => { setPalette(true); setDrawer(false); }}
        title="Search (⌘K)"
        style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, cursor: "pointer", fontSize: 13,
          justifyContent: collapsed ? "center" : "flex-start" }}>
        <Search size={14} /> {!collapsed && <>Search <span className="k-muted" style={{ marginLeft: "auto", fontSize: 11 }}>⌘K</span></>}
      </button>
      <nav style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {!collapsed && <ProjectSwitcher orgId={org?.id} />}
        {items.map(it => (
          <NavLink key={it.key} to={it.to} data-testid={`nav-${it.key}`} onClick={() => setDrawer(false)}
            title={collapsed ? it.label : undefined}
            style={{ display: "flex", alignItems: "center", gap: 10,
              padding: collapsed ? "10px 0" : (it.sub ? "8px 12px 8px 30px" : "10px 12px"),
              justifyContent: collapsed ? "center" : "flex-start",
              borderRadius: 10, textDecoration: "none",
              borderLeft: active === it.key ? "2px solid var(--accent-primary)" : "2px solid transparent",
              background: active === it.key ? "var(--control)" : "transparent",
              color: active === it.key ? "var(--accent-primary)" : "var(--muted)", fontWeight: 500 }}>
            <it.icon size={17} /> {!collapsed && it.label}
          </NavLink>
        ))}
      </nav>
      {!collapsed && <div style={{ marginTop: 16 }}><RecentActivity /></div>}
      <div style={{ marginTop: "auto", paddingTop: 24 }}>
        {collapsed ? (
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
            <Avatar name={user?.name} email={user?.email} size={28} testid="user-avatar" />
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <Avatar name={user?.name} email={user?.email} size={28} testid="user-avatar" />
            <span className="k-muted k-truncate" style={{ fontSize: 12 }} title={user?.email}>{user?.email}</span>
          </div>
        )}
        <button className="k-btn k-btn-ghost" data-testid="logout-btn" title="Sign out"
          style={{ width: "100%", justifyContent: "center" }}
          onClick={async () => { await logout(); nav("/login"); }}>
          <LogOut size={15} /> {!collapsed && "Sign out"}
        </button>
      </div>
    </>
  );

  const isAdmin = user?.platform_role === "admin";
  // Patch 64 — the builder dashboard's left rail is replaced by this compact icon nav strip. Every page
  // the rail used to reach gets one labelled icon here, with the Summary/Expanded tooltip depth (Patch 62).
  const NAV_EXPANDED = {
    workspace: "Your Workspace — the project list and dashboard home.",
    marketplace: "Marketplace — browse and add solution templates and connectors.",
    hiring: "Hire — find vetted help to build or extend your app.",
    hosting: "Hosting & data — provision and manage your app's deployment.",
    mobile: "Mobile — configure iOS/Android build targets.",
    ussd: "USSD — set up USSD access for feature-phone users.",
    media: "Media — manage uploads, audio and video assets.",
    billing: "Billing — credits, top-ups, invoices and billing history.",
    profile: "Profile — your account details, email and password.",
  };
  const navStrip = (
    <div data-testid="hub-nav-strip" className="k-container" style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", marginBottom: 14, flexWrap: "wrap" }}>
      <span className="k-heading" data-testid="hub-home" onClick={() => nav("/workspace")} title="Workspace home"
        style={{ fontWeight: 800, fontSize: 18, letterSpacing: "-.02em", cursor: "pointer", borderBottom: "3px solid var(--accent-primary)", paddingBottom: 2, marginRight: 6 }}>Kadana</span>
      <DepthTip testid="navtip-search" side="bottom" summary="Search everything (⌘K)." expanded="The command palette — one unified search across your apps, connectors and dashboard actions. Shortcut: ⌘K / Ctrl+K.">
        <button className="k-btn k-btn-ghost" data-testid="nav-search" aria-label="Search (Command palette)" onClick={() => setPalette(true)}
          style={{ padding: "7px 9px", minHeight: 0 }}><Search size={16} /></button>
      </DepthTip>
      <div style={{ position: "relative" }}>
        <DepthTip testid="navtip-apps" side="bottom" summary="Apps — Marketplace, Hire, Hosting, Mobile, USSD, Media." expanded="One entry point to all six product areas; opens a compact grid. The pages are unchanged — only their entry point is consolidated from six top-level icons to one.">
          <button className="k-btn k-btn-ghost" data-testid="nav-apps" aria-label="Apps" onClick={() => setAppsOpen(o => !o)} style={{ padding: "7px 9px", minHeight: 0 }}><LayoutGrid size={16} /></button>
        </DepthTip>
        {appsOpen && (
          <div className="k-container" data-testid="apps-sheet" style={{ position: "absolute", left: 0, top: "calc(100% + 8px)", width: 300, padding: 12, zIndex: 80, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {items.filter(it => ["marketplace", "hiring", "hosting", "mobile", "ussd", "media"].includes(it.key)).map(it => (
              <button key={it.key} className="k-btn k-btn-ghost" data-testid={`apps-${it.key}`} onClick={() => { setAppsOpen(false); nav(it.to); }}
                style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-start", padding: "8px 10px" }}><it.icon size={15} /> {it.label}</button>
            ))}
          </div>
        )}
      </div>
      {toolbar && <div data-testid="hub-toolbar" style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>{toolbar}</div>}
      {bare && <div style={{ marginLeft: "auto" }}><TopRightCluster /></div>}
    </div>
  );

  return (
    <div className="k-canvas" style={{ display: "flex" }}>
      {/* Desktop: static sticky sidebar — kept ONLY for the Super Admin Console (admins). The ordinary
          builder's dashboard has no left rail (Patch 64); it navigates via the icon strip in <main>. */}
      {!isMobile && isAdmin && (
        <aside data-testid="desktop-sidebar" style={{ width: collapsed ? 72 : 240, borderRight: "1px solid var(--border)",
          padding: collapsed ? "22px 10px" : "22px 16px",
          position: "sticky", top: 0, height: "100vh", overflowY: "auto", background: "var(--container)",
          display: "flex", flexDirection: "column", transition: "width .2s ease" }}>
          {renderSidebar(collapsed)}
        </aside>
      )}

      {/* Mobile: fixed top bar with a hamburger + a slide-in drawer. */}
      {isMobile && (
        <>
          <div data-testid="mobile-topbar" style={{ position: "fixed", top: 0, left: 0, right: 0, height: 56, zIndex: 55,
            display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 14px",
            background: "var(--container)", borderBottom: "1px solid var(--border)" }}>
            <button className="k-btn k-btn-ghost" data-testid="mobile-menu-open" aria-label="Open menu"
              style={{ padding: "8px 11px", minHeight: 0 }} onClick={() => setDrawer(true)}><Menu size={18} /></button>
            <span className="k-heading" onClick={() => nav("/workspace")} style={{ fontWeight: 800, fontSize: 20, letterSpacing: "-.02em",
              cursor: "pointer", borderBottom: "3px solid var(--accent-primary)", paddingBottom: 2 }}>Kadana</span>
            <button className="k-btn k-btn-ghost" data-testid="mobile-search-open" aria-label="Search"
              style={{ padding: "8px 11px", minHeight: 0 }} onClick={() => setPalette(true)}><Search size={17} /></button>
          </div>
          {drawer && <div data-testid="mobile-drawer-backdrop" onClick={() => setDrawer(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 60 }} />}
          <aside data-testid="mobile-drawer" style={{ position: "fixed", top: 0, left: 0, height: "100vh", width: 280, zIndex: 61,
            background: "var(--container)", borderRight: "1px solid var(--border)", padding: "18px 16px", overflowY: "auto",
            transform: drawer ? "translateX(0)" : "translateX(-100%)", transition: "transform .25s cubic-bezier(.4,0,.2,1)",
            boxShadow: drawer ? "8px 0 30px rgba(0,0,0,.4)" : "none" }}>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
              <button className="k-btn k-btn-ghost" data-testid="mobile-drawer-close" aria-label="Close menu"
                style={{ padding: "6px 10px", minHeight: 0 }} onClick={() => setDrawer(false)}><X size={16} /></button>
            </div>
            {renderSidebar(false)}
          </aside>
        </>
      )}

      <main style={{ flex: 1, padding: isMobile ? "72px 16px 0" : (bare ? "14px 12px 8px" : "34px 24px 0"), width: "100%",
        margin: "0 auto", display: "flex", flexDirection: "column", minWidth: 0, height: bare && !isMobile ? "100vh" : undefined }}>
        {bare && !isMobile ? (
          <>
            {!isAdmin && navStrip}
            <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>{children}</div>
          </>
        ) : (
          <div style={{ flex: 1 }}>{!isMobile && !isAdmin && navStrip}<TopRightCluster /><Breadcrumbs /><DemoBanner /><WhatsNew />{children}</div>
        )}
      </main>
      <RegistryDrawer open={palette} onClose={() => setPalette(false)} admin={user?.platform_role === "admin"} />
      <ShortcutHelp />
      <SessionWatch />
      <BackToTop />
    </div>
  );
}
